import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../api/client";
import { formatMoney } from "../lib/format";

type Kind = "pws" | "home" | "region" | "funder" | "month" | "claimant" | "status" | "acuity" | "rag" | "cqc";

const TABS: { kind: Kind; label: string }[] = [
  { kind: "pws",      label: "Per PWS" },
  { kind: "home",     label: "Per home" },
  { kind: "region",   label: "Per region" },
  { kind: "funder",   label: "Per commissioner" },
  { kind: "month",    label: "By month" },
  { kind: "claimant", label: "By claimant" },
  { kind: "status",   label: "By status" },
  { kind: "acuity",   label: "By acuity" },
  { kind: "rag",      label: "By RAG" },
  { kind: "cqc",      label: "By CQC rating" },
];

export function Reports() {
  const [kind, setKind] = useState<Kind>("home");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const params: Record<string, string> = {};
  if (from) params.from = from;
  if (to)   params.to   = to;

  const r = useQuery({ queryKey: ["report", kind, from, to], queryFn: () => api.report(kind, params) });
  const rows: any[] = r.data?.rows ?? [];
  const summary = useMemo(() => {
    const totalCount = rows.reduce((s, x) => s + Number(x.estimate_count ?? 0), 0);
    const approvedValue = rows.reduce((s, x) => s + Number(x.approved_value ?? x.total_value ?? 0), 0);
    return { totalCount, approvedValue };
  }, [rows]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-semibold text-liaise-navy">Reports</h1>
        <p className="text-slate-500 mt-1">Aggregates across every estimate. Counts include drafts; £ values only count approved.</p>
      </div>

      <div className="flex gap-3 flex-wrap items-end">
        <div>
          <div className="text-xs text-slate-500 mb-1">Dimension</div>
          <select value={kind} onChange={(e) => setKind(e.target.value as Kind)}
                  className="px-3 py-2 border border-slate-200 rounded text-sm bg-white">
            {TABS.map((t) => <option key={t.kind} value={t.kind}>{t.label}</option>)}
          </select>
        </div>
        <div>
          <div className="text-xs text-slate-500 mb-1">From</div>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
                 className="px-3 py-2 border border-slate-200 rounded text-sm bg-white" />
        </div>
        <div>
          <div className="text-xs text-slate-500 mb-1">To</div>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)}
                 className="px-3 py-2 border border-slate-200 rounded text-sm bg-white" />
        </div>
        <div className="ml-auto text-sm text-slate-500">
          {summary.totalCount} estimates · {formatMoney(summary.approvedValue)} approved
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-x-auto">
        {r.isLoading && <div className="p-6 text-slate-500 text-sm">Loading...</div>}
        {r.error && <div className="p-6 text-red-600 text-sm">{(r.error as Error).message}</div>}
        {r.data && rows.length === 0 && <div className="p-6 text-slate-500 text-sm">No data for this filter.</div>}
        {r.data && rows.length > 0 && <ReportTable kind={kind} rows={rows} />}
      </div>
    </div>
  );
}

function ReportTable({ kind, rows }: { kind: Kind; rows: any[] }) {
  const cols = columnsFor(kind);
  return (
    <table className="w-full text-sm">
      <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
        <tr>
          {cols.map((c) => (
            <th key={c.key} className={`px-4 py-2 ${c.right ? "text-right" : "text-left"}`}>{c.label}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i} className="border-t border-slate-100 hover:bg-slate-50">
            {cols.map((c) => (
              <td key={c.key} className={`px-4 py-2 ${c.right ? "text-right tabular-nums" : ""}`}>
                {c.render ? c.render(row) : row[c.key]}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

interface Col {
  key: string;
  label: string;
  right?: boolean;
  render?: (row: any) => any;
}

function columnsFor(kind: Kind): Col[] {
  const counts: Col[] = [
    { key: "estimate_count", label: "Total", right: true },
    { key: "drafts",         label: "Drafts", right: true },
    { key: "submitted",      label: "In review", right: true },
    { key: "approved",       label: "Approved", right: true },
    { key: "rejected",       label: "Rejected", right: true },
    { key: "approved_value", label: "Approved £", right: true, render: (r) => formatMoney(r.approved_value) },
  ];
  switch (kind) {
    case "pws": return [
      { key: "name", label: "Person we support", render: (r) => `${r.name ?? r.pws_initials ?? "-"}${r.pws_initials ? ` (${r.pws_initials})` : ""}` },
      { key: "home_name", label: "Home" },
      { key: "partnership_rag", label: "RAG" },
      { key: "acuity", label: "Acuity" },
      ...counts,
    ];
    case "home": return [
      { key: "name", label: "Home" },
      { key: "region", label: "Region" },
      { key: "cqc_rating", label: "CQC" },
      { key: "type_of_service", label: "Service type" },
      ...counts,
    ];
    case "region": return [
      { key: "region", label: "Region" },
      ...counts,
    ];
    case "funder": return [
      { key: "name", label: "Commissioner" },
      { key: "funder_type", label: "Type" },
      { key: "partnership_rag", label: "RAG" },
      { key: "case_by_case", label: "Case by case", render: (r) => (r.case_by_case ? "Yes" : "") },
      { key: "fy27_uplift_pct", label: "FY27 uplift %", right: true },
      ...counts,
    ];
    case "month": return [
      { key: "month", label: "Month" },
      ...counts,
    ];
    case "claimant": return [
      { key: "name", label: "Claimant", render: (r) => (
        <Link to={`/claimant/${r.oid}`} className="text-liaise-blue hover:underline">{r.name ?? r.email}</Link>
      )},
      { key: "email", label: "Email" },
      ...counts,
    ];
    case "status": return [
      { key: "status", label: "Status" },
      { key: "estimate_count", label: "Count", right: true },
      { key: "total_value", label: "Total £", right: true, render: (r) => formatMoney(r.total_value) },
    ];
    case "acuity": return [
      { key: "acuity", label: "Acuity" },
      ...counts,
    ];
    case "rag": return [
      { key: "partnership_rag", label: "RAG" },
      ...counts,
    ];
    case "cqc": return [
      { key: "cqc_rating", label: "CQC rating" },
      ...counts,
    ];
  }
}
